import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Image as ImageIcon, FileText, File as FileIcon, FileArchive, FileAudio, FileVideo, Download, X, } from 'lucide-react';
import { Button, Dialog, DialogClose, DialogContent, DialogOverlay, DialogPortal, DialogTitle, } from '@wit-support/ui';
import { cn } from './lib/cn.js';
import { bytesToHuman } from './lib/format-bytes.js';
/**
 * Module-level base URL for the admin-api that serves attachment bytes. Set
 * once at app bootstrap (see `apps/admin-web/src/app.tsx`). We use a module
 * singleton instead of React context because `ui-domain` is consumed by
 * several packages that shouldn't each have to plumb a provider.
 */
let attachmentsBaseUrl = '';
export function setAttachmentsBaseUrl(url) {
    attachmentsBaseUrl = url;
}
export function getAttachmentUrl(id) {
    return attachmentsBaseUrl ? `${attachmentsBaseUrl}/api/attachments/${id}` : '';
}
function categoryOf(ct) {
    if (!ct)
        return 'file';
    if (ct.startsWith('image/'))
        return 'image';
    if (ct === 'application/pdf')
        return 'pdf';
    if (ct.startsWith('audio/'))
        return 'audio';
    if (ct.startsWith('video/'))
        return 'video';
    if (ct.startsWith('text/') || ct === 'application/json' || ct === 'application/xml')
        return 'text';
    if (ct === 'application/zip' || ct.includes('compressed') || ct.includes('archive'))
        return 'archive';
    return 'file';
}
function iconFor(category) {
    switch (category) {
        case 'image':
            return ImageIcon;
        case 'pdf':
        case 'text':
            return FileText;
        case 'audio':
            return FileAudio;
        case 'video':
            return FileVideo;
        case 'archive':
            return FileArchive;
        default:
            return FileIcon;
    }
}
/** Category is previewable inline — we open a lightbox. Otherwise it's a pure download. */
function isPreviewable(category) {
    return (category === 'image' ||
        category === 'pdf' ||
        category === 'audio' ||
        category === 'video' ||
        category === 'text');
}
function middleEllipsis(name, max = 28) {
    if (name.length <= max)
        return name;
    const head = Math.ceil((max - 1) / 2);
    const tail = Math.floor((max - 1) / 2);
    return `${name.slice(0, head)}…${name.slice(-tail)}`;
}
function extensionOf(filename) {
    const dot = filename.lastIndexOf('.');
    if (dot <= 0 || dot === filename.length - 1)
        return '';
    return filename.slice(dot + 1).toUpperCase();
}
const TILE_SIZE = 'h-[140px] w-[140px]';
/**
 * Uniform square attachment tile. Every attachment — image, PDF, audio, video,
 * archive, unknown — renders at the same 140×140 footprint so messages look
 * tidy regardless of file mix. Click:
 *  - Previewable types (image/pdf/audio/video/text) → inline `Lightbox` modal.
 *  - Non-previewable → download immediately.
 *
 * The tile itself is a shadcn-style `Card` element; the lightbox reuses the
 * shared `Dialog` primitive. No bespoke chrome, only UIKit + lucide.
 */
export const AttachmentLink = React.forwardRef(({ attachment, className }, ref) => {
    const url = getAttachmentUrl(attachment.id);
    const category = categoryOf(attachment.contentType);
    const filename = attachment.filename ?? 'attachment';
    const sizeLabel = bytesToHuman(attachment.sizeBytes);
    const preview = isPreviewable(category) && !!url;
    const [open, setOpen] = React.useState(false);
    const tileBody = (_jsx(TileSurface, { category: category, url: url, filename: filename, sizeLabel: sizeLabel }));
    const tileClass = cn('group relative block overflow-hidden rounded-[var(--radius-3)] border border-[var(--color-border-default)] bg-[var(--color-surface-sunken)] text-left transition-colors hover:border-[var(--color-border-strong)] hover:bg-[var(--color-surface-raised)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]', TILE_SIZE, className);
    if (!preview) {
        // Non-previewable or no URL yet — plain download anchor so the browser's
        // native download flow (Content-Disposition: attachment) kicks in.
        return (_jsx("a", { ref: ref, href: url || '#', download: filename, "aria-disabled": !url, title: filename, className: tileClass, children: tileBody }));
    }
    return (_jsxs(_Fragment, { children: [_jsx("button", { ref: ref, type: "button", onClick: () => setOpen(true), title: filename, "aria-label": `Preview ${filename}`, className: tileClass, children: tileBody }), _jsx(AttachmentLightbox, { open: open, onOpenChange: setOpen, category: category, url: url, filename: filename, sizeLabel: sizeLabel })] }));
});
AttachmentLink.displayName = 'AttachmentLink';
/**
 * Inside-the-tile content. Images get a cover thumbnail; everything else gets
 * a centered icon + extension label. Filename + size footer is shared.
 */
const TileSurface = ({ category, url, filename, sizeLabel }) => {
    const Icon = iconFor(category);
    const ext = extensionOf(filename) || category.toUpperCase();
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "flex h-[92px] w-full items-center justify-center bg-[var(--color-surface-canvas)]", children: category === 'image' && url ? (_jsx("img", { src: url, alt: filename, loading: "lazy", className: "h-full w-full object-cover transition-transform group-hover:scale-[1.02]" })) : (_jsxs("div", { className: "flex flex-col items-center gap-1", children: [_jsx(Icon, { className: "h-8 w-8 text-[var(--color-text-muted)]", "aria-hidden": "true" }), _jsx("span", { className: "text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]", children: ext })] })) }), _jsxs("div", { className: "flex flex-col gap-0.5 border-t border-[var(--color-border-subtle)] px-2 py-1.5", children: [_jsx("span", { className: "truncate text-xs font-medium text-[var(--color-text-primary)]", children: middleEllipsis(filename, 22) }), sizeLabel ? (_jsx("span", { className: "text-[10px] text-[var(--color-text-muted)]", children: sizeLabel })) : null] })] }));
};
/**
 * Lightbox built on the shared `Dialog` primitive. Overrides DialogContent's
 * default centered-card shape with a full-viewport (~92vw × 90dvh) surface so
 * media previews don't get cropped. Close affordance + download live in the
 * sticky header.
 */
const AttachmentLightbox = ({ open, onOpenChange, category, url, filename, sizeLabel, }) => {
    return (_jsx(Dialog, { open: open, onOpenChange: onOpenChange, children: _jsxs(DialogPortal, { children: [_jsx(DialogOverlay, { className: "bg-[var(--color-scrim)]" }), _jsxs(DialogContent, { className: "left-1/2 top-1/2 h-[90dvh] w-[min(92vw,1100px)] max-w-none -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[var(--radius-3)] bg-[var(--color-surface-raised)] p-0", "aria-describedby": undefined, children: [_jsxs("header", { className: "flex items-center gap-3 border-b border-[var(--color-border-subtle)] bg-[var(--color-surface-raised)] px-4 py-2", children: [_jsx(DialogTitle, { className: "min-w-0 flex-1 truncate text-sm font-medium", title: filename, children: filename }), sizeLabel ? (_jsx("span", { className: "hidden shrink-0 text-xs text-[var(--color-text-muted)] sm:inline", children: sizeLabel })) : null, _jsx(Button, { asChild: true, variant: "ghost", size: "sm", children: _jsxs("a", { href: url, download: filename, children: [_jsx(Download, { className: "h-4 w-4", "aria-hidden": "true" }), " Download"] }) }), _jsx(DialogClose, { asChild: true, children: _jsx(Button, { variant: "ghost", size: "sm", "aria-label": "Close preview", children: _jsx(X, { className: "h-4 w-4", "aria-hidden": "true" }) }) })] }), _jsx("div", { className: "flex min-h-0 flex-1 items-center justify-center overflow-auto bg-[var(--color-surface-canvas)]", children: _jsx(LightboxPreview, { category: category, url: url, filename: filename }) })] })] }) }));
};
const LightboxPreview = ({ category, url, filename }) => {
    switch (category) {
        case 'image':
            return _jsx("img", { src: url, alt: filename, className: "max-h-full max-w-full object-contain" });
        case 'pdf':
            return (_jsx("iframe", { src: url, title: filename, className: "h-full w-full bg-[var(--color-email-canvas)]" }));
        case 'video':
            return _jsx("video", { src: url, controls: true, autoPlay: true, className: "max-h-full max-w-full" });
        case 'audio':
            return (_jsxs("div", { className: "flex w-full max-w-xl flex-col gap-3 p-6", children: [_jsx("audio", { src: url, controls: true, autoPlay: true, className: "w-full" }), _jsx("p", { className: "text-sm text-[var(--color-text-muted)]", children: filename })] }));
        case 'text':
            return _jsx(TextBlobPreview, { url: url });
        default:
            return (_jsx("p", { className: "p-6 text-sm text-[var(--color-text-muted)]", children: "Preview not available for this file type. Use Download to open it." }));
    }
};
/** Fetches the first 64 KB of a text-like blob and renders it in a <pre>. */
const TextBlobPreview = ({ url }) => {
    const [text, setText] = React.useState(null);
    const [err, setErr] = React.useState(null);
    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(url, { credentials: 'include' });
                if (!res.ok)
                    throw new Error(`HTTP ${res.status}`);
                const blob = await res.blob();
                const slice = blob.slice(0, 64 * 1024);
                const t = await slice.text();
                if (!cancelled)
                    setText(t);
            }
            catch (e) {
                if (!cancelled)
                    setErr(e instanceof Error ? e.message : String(e));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [url]);
    if (err) {
        return _jsxs("p", { className: "p-4 text-xs text-[var(--color-danger-fg)]", children: ["Preview failed: ", err] });
    }
    if (text == null) {
        return _jsx("p", { className: "p-4 text-xs text-[var(--color-text-muted)]", children: "Loading preview\u2026" });
    }
    return (_jsx("pre", { className: "h-full w-full overflow-auto bg-[var(--color-surface-sunken)] px-4 py-3 text-xs whitespace-pre-wrap break-words", children: text }));
};
//# sourceMappingURL=attachment-link.js.map